import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  NavLink,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Login } from "./pages/Login";
import { StoreCount } from "./pages/StoreCount";
import { AdminOverview } from "./pages/AdminOverview";
import { AdminMonthEnd } from "./pages/AdminMonthEnd";
import { AdminStoreDay } from "./pages/AdminStoreDay";
import { AdminCatalog } from "./pages/AdminCatalog";
import { AdminUsers } from "./pages/AdminUsers";
import { AdminTrends } from "./pages/AdminTrends";
import { AdminAudit } from "./pages/AdminAudit";
import { apiClient } from "./api/client";

const queryClient = new QueryClient();

function getUser() {
  return JSON.parse(localStorage.getItem("user") || "null");
}

function ProtectedRoute({
  children,
  requiredRole,
}: {
  children: React.ReactNode;
  requiredRole?: string;
}) {
  const user = getUser();

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return (
      <Navigate to={user.role === "ADMIN" ? "/admin/overview" : "/store"} />
    );
  }

  return <>{children}</>;
}

function NavBar() {
  const user = getUser();

  const handleLogout = async () => {
    await apiClient.logout();
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  if (!user) return null;

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? "app-nav-link is-active" : "app-nav-link";

  return (
    <nav className="app-nav">
      <div className="app-nav-left">
        <div className="app-brand">
          <h2>Perpetual Stock</h2>
          {user.store && (
            <p className="app-brand-subtitle">
              {user.store.name}
            </p>
          )}
        </div>
        {user.role === "ADMIN" && (
          <div className="app-nav-links">
            <NavLink to="/admin/overview" className={navLinkClass}>
              Overview
            </NavLink>
            <NavLink to="/admin/month-end" className={navLinkClass}>
              Month-End
            </NavLink>
            <NavLink to="/admin/catalog" className={navLinkClass}>
              Catalog
            </NavLink>
            <NavLink to="/admin/trends" className={navLinkClass}>
              Trends
            </NavLink>
            <NavLink to="/admin/audit" className={navLinkClass}>
              Audit
            </NavLink>
            <NavLink to="/admin/users" className={navLinkClass}>
              Users & Stores
            </NavLink>
          </div>
        )}
      </div>
      <div className="app-nav-right">
        <span className="app-user-email">{user.email}</span>
        <button
          onClick={handleLogout}
          className="app-logout-button"
        >
          Logout
        </button>
      </div>
    </nav>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <NavBar />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/store"
            element={
              <ProtectedRoute requiredRole="STORE">
                <StoreCountWrapper />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/overview"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AdminOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/month-end"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AdminMonthEnd />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/catalog"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AdminCatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trends"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AdminTrends />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AdminAudit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/store/:storeId"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AdminStoreDay />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/store" />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

function StoreCountWrapper() {
  const user = getUser();
  return user?.store ? (
    <StoreCount storeId={user.store.id} />
  ) : (
    <div>Loading...</div>
  );
}

export default App;
